import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Phone, Mail, MapPin, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { pathwayOptions } from '../../mock';
import { createBooking, readableError } from '../../lib/api';

const BookingForm = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    pathway: pathwayOptions[0],
    goals: '',
    consent: false,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.consent) {
      toast({ title: 'Please complete required fields', description: 'Name, email and consent are required.' });
      return;
    }
    try {
      setSubmitting(true);
      await createBooking({
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        pathway: form.pathway,
        goals: form.goals,
        consent: form.consent,
      });
      setSubmitted(true);
      toast({ title: 'Enquiry received', description: 'Our team will be in touch within one business day.' });
    } catch (err) {
      toast({ title: 'Unable to submit', description: readableError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" className="py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="rounded-[32px] overflow-hidden border border-[rgba(31,58,46,0.1)] bg-[var(--brand-green)] text-[var(--brand-cream)] relative">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="grid lg:grid-cols-12 relative">
            <div className="lg:col-span-5 p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-[rgba(247,243,236,0.15)]">
              <div className="text-xs uppercase tracking-[0.24em] text-[var(--brand-gold-2)] mb-4">Book your consultation</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Your health is your <em className="italic">greatest</em> asset.
              </h2>
              <p className="mt-5 opacity-85 text-lg max-w-md">
                Submit an enquiry and our team will call you back within one business day to help you choose the right pathway.
              </p>

              <div className="mt-8 space-y-3 text-sm opacity-90">
                <div className="flex items-center gap-3"><Phone size={14} /> <a className="underline underline-offset-4" href="tel:+61432809498">+61 432 809 498</a></div>
                <div className="flex items-center gap-3"><Mail size={14} /> <a className="underline underline-offset-4" href="mailto:reception@elevatemedicalclinics.com.au">reception@elevatemedicalclinics.com.au</a></div>
                <div className="flex items-center gap-3"><MapPin size={14} /> Unit 6, 689 Beaufort St, Mount Lawley WA</div>
              </div>
            </div>

            <div className="lg:col-span-7 p-8 lg:p-12">
              {submitted ? (
                <div className="h-full flex flex-col justify-center items-start">
                  <div className="h-14 w-14 rounded-full bg-[var(--brand-gold)] text-[var(--brand-green)] grid place-items-center">
                    <CheckCircle2 size={26} />
                  </div>
                  <div className="font-serif text-3xl mt-5">Thank you, {form.firstName}.</div>
                  <p className="mt-3 opacity-85 max-w-md">
                    Your enquiry has been received. A member of our team will reach out within one business day to confirm your consultation.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ firstName: '', lastName: '', email: '', phone: '', pathway: pathwayOptions[0], goals: '', consent: false });
                    }}
                    className="mt-8 btn-outline text-sm !text-[var(--brand-cream)] !border-[var(--brand-cream)] hover:!bg-[var(--brand-cream)] hover:!text-[var(--brand-green)]"
                  >
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
                  <Field label="First name*" v={form.firstName} onChange={(v) => update('firstName', v)} />
                  <Field label="Last name" v={form.lastName} onChange={(v) => update('lastName', v)} />
                  <Field label="Email*" type="email" v={form.email} onChange={(v) => update('email', v)} />
                  <Field label="Phone" type="tel" v={form.phone} onChange={(v) => update('phone', v)} />

                  <div className="sm:col-span-2">
                    <Label>Preferred pathway</Label>
                    <select
                      value={form.pathway}
                      onChange={(e) => update('pathway', e.target.value)}
                      className="w-full bg-transparent border border-[rgba(247,243,236,0.25)] rounded-xl px-4 py-3.5 text-[var(--brand-cream)] focus:outline-none focus:border-[var(--brand-gold-2)]"
                    >
                      {pathwayOptions.map((o) => (
                        <option key={o} value={o} className="text-[var(--brand-ink)]">{o}</option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <Label>Tell us about your goals</Label>
                    <textarea
                      value={form.goals}
                      onChange={(e) => update('goals', e.target.value)}
                      rows={4}
                      placeholder="e.g. Better metabolic health, weight loss, resolve fatigue, hormone balance…"
                      className="w-full bg-transparent border border-[rgba(247,243,236,0.25)] rounded-xl px-4 py-3.5 text-[var(--brand-cream)] placeholder:text-[rgba(247,243,236,0.45)] focus:outline-none focus:border-[var(--brand-gold-2)] resize-none"
                    />
                  </div>

                  <label className="sm:col-span-2 flex items-start gap-3 text-sm opacity-85 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={(e) => update('consent', e.target.checked)}
                      className="mt-1 h-4 w-4 accent-[var(--brand-gold)]"
                    />
                    <span>I consent to Elevate Medical Clinics contacting me regarding my enquiry and understand this is not medical advice.</span>
                  </label>

                  <div className="sm:col-span-2 mt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary !bg-[var(--brand-gold)] !border-[var(--brand-gold)] !text-[var(--brand-green)] hover:!bg-[var(--brand-gold-2)] disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {submitting ? (<><Loader2 size={16} className="animate-spin" /> Submitting…</>) : (<>Submit enquiry <ArrowRight size={16} /></>)}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Label = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--brand-gold-2)] mb-2">{children}</div>
);

const Field = ({ label, v, onChange, type = 'text' }) => (
  <div>
    <Label>{label}</Label>
    <input
      type={type}
      value={v}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border border-[rgba(247,243,236,0.25)] rounded-xl px-4 py-3.5 text-[var(--brand-cream)] placeholder:text-[rgba(247,243,236,0.45)] focus:outline-none focus:border-[var(--brand-gold-2)]"
    />
  </div>
);

export default BookingForm;
